import {
    cloneElement,
    isValidElement,
    useEffect,
    useId,
    useRef,
    useState,
    type KeyboardEvent,
    type MouseEvent,
    type ReactElement,
    type ReactNode,
} from 'react';

import { cn } from '../lib/cn';

export interface MenuItem {
    label: ReactNode;
    icon?: ReactNode;
    onSelect: () => void;
    /** Disabled rows render greyed out and don't fire onSelect. */
    disabled?: boolean;
    /** Highlighted in red — for destructive actions like delete / merge. */
    destructive?: boolean;
}

export interface MenuProps {
    /**
     * The clickable trigger. Should be a single React element accepting
     * `onClick`. We clone it to inject our toggle handler so callers can
     * pass a bare <Button> or <button> without ceremony.
     */
    trigger: ReactElement;
    items: MenuItem[];
    /** Right-align the panel under the trigger (default) vs left-align. */
    align?: 'start' | 'end';
    /** Tailwind width class for the panel. Defaults to a comfortable width. */
    panelClassName?: string;
}

// Lightweight dropdown menu. Keyboard model: Enter/Space on trigger opens
// (focuses first item), Esc closes, ArrowUp/Down moves between items, click
// outside closes. We're using an absolute panel rather than a portal so
// nested-stacking-context issues stay simple — every consumer of this lives
// outside dialogs/modals so far.
export function Menu({ trigger, items, align = 'end', panelClassName }: MenuProps) {
    const [open, setOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);
    const containerRef = useRef<HTMLDivElement>(null);
    const itemRefs = useRef<Array<HTMLButtonElement | null>>([]);
    const panelId = useId();

    useEffect(() => {
        if (!open) return;
        const onKey = (e: globalThis.KeyboardEvent) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                setOpen(false);
            }
        };
        const onClickOutside = (e: globalThis.MouseEvent) => {
            if (!containerRef.current?.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener('keydown', onKey);
        document.addEventListener('mousedown', onClickOutside);
        return () => {
            document.removeEventListener('keydown', onKey);
            document.removeEventListener('mousedown', onClickOutside);
        };
    }, [open]);

    // Focus the active item every time it changes (or the menu opens).
    useEffect(() => {
        if (!open) return;
        const node = itemRefs.current[activeIndex];
        node?.focus();
    }, [open, activeIndex]);

    const onTriggerClick = () => {
        if (open) {
            setOpen(false);
        } else {
            const firstEnabled = items.findIndex((it) => !it.disabled);
            setActiveIndex(firstEnabled === -1 ? 0 : firstEnabled);
            setOpen(true);
        }
    };

    const onPanelKey = (e: KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            const next = items
                .map((it, i) => (it.disabled ? -1 : i))
                .filter((i) => i > activeIndex)[0];
            if (next != null) setActiveIndex(next);
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            const prev = items
                .map((it, i) => (it.disabled ? -1 : i))
                .filter((i) => i >= 0 && i < activeIndex)
                .pop();
            if (prev != null) setActiveIndex(prev);
        } else if (e.key === 'Tab') {
            // Tabbing out closes — let the natural tab order take over.
            setOpen(false);
        }
    };

    const handleSelect = (item: MenuItem) => {
        if (item.disabled) return;
        setOpen(false);
        item.onSelect();
    };

    // Clone the trigger so we own its onClick + aria-expanded.
    const triggerElement = isValidElement(trigger)
        ? cloneElement(trigger, {
              onClick: (e: MouseEvent<HTMLElement>) => {
                  // Preserve any onClick the consumer passed.
                  const original = (trigger.props as { onClick?: (e: MouseEvent<HTMLElement>) => void })
                      .onClick;
                  original?.(e);
                  if (!e.defaultPrevented) onTriggerClick();
              },
              'aria-haspopup': 'menu' as const,
              'aria-expanded': open,
              'aria-controls': open ? panelId : undefined,
          } as Record<string, unknown>)
        : trigger;

    return (
        <div ref={containerRef} className="relative inline-block">
            {triggerElement}
            {open && (
                <div
                    id={panelId}
                    role="menu"
                    onKeyDown={onPanelKey}
                    className={cn(
                        'absolute top-full z-30 mt-1 min-w-[10rem] overflow-hidden rounded-md border border-solid border-gray-200 bg-white py-1 text-sm shadow-lg dark:border-gray-700 dark:bg-gray-800',
                        align === 'end' ? 'right-0' : 'left-0',
                        panelClassName,
                    )}
                >
                    {items.map((item, i) => (
                        <button
                            key={i}
                            ref={(el) => {
                                itemRefs.current[i] = el;
                            }}
                            type="button"
                            role="menuitem"
                            disabled={item.disabled}
                            onMouseEnter={() => !item.disabled && setActiveIndex(i)}
                            onClick={() => handleSelect(item)}
                            className={cn(
                                'flex w-full items-center gap-2 px-3 py-2 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-50',
                                i === activeIndex && !item.disabled
                                    ? item.destructive
                                        ? 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-200'
                                        : 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-200'
                                    : item.destructive
                                      ? 'text-red-700 dark:text-red-300'
                                      : 'text-gray-700 dark:text-gray-200',
                            )}
                        >
                            {item.icon && (
                                <span aria-hidden="true" className="shrink-0">
                                    {item.icon}
                                </span>
                            )}
                            <span className="flex-1">{item.label}</span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
